'use strict'

/**
 * Directed graph
 */
export class Graph {
    constructor() {
        /** @type {Map<any, Graph.Vertex>} */
        this._vertices = new Map();

        /** @type {Graph.Edge[]} */
        this._edges = [];

        /** @type {Map<Graph.Vertex, Graph.Edge[]>} */
        this._edgesToVertex = new Map();
    }

    /**
     * @param {any} obj
     * @returns {Graph.Vertex}
     */
    putVertex(obj) {
        let vertex = this.getVertex(obj);
        if (!vertex) {
            vertex = new Graph.Vertex(obj);
            this._vertices.set(obj, vertex);
        }
        return vertex;
    }

    /**
     * @param {any} from
     * @param {any} to
     * @returns {Graph.Edge}
     */
    addEdge(from, to) {
        const vFrom = this._getOrCreateVertex(from);
        const vTo = this._getOrCreateVertex(to);
        const edge = new Graph.Edge(vFrom, vTo);
        if (!this.canCreateEdge(edge))
            return edge;
        if (!this.hasVertex(vFrom))
            this._vertices.set(vFrom.obj, vFrom);
        if (!this.hasVertex(vTo))
            this._vertices.set(vTo.obj, vTo);
        vFrom.addEdge(edge);
        vTo.addEdge(edge);
        this._edges.push(edge);
        if (!this._edgesToVertex.has(vTo))
            this._edgesToVertex.set(vTo, []);
        this._edgesToVertex.get(vTo).push(edge);
        return edge;
    };

    /** @param {Graph.Vertex || any} vertex */
    removeVertex(vertex) {
        if (!(vertex instanceof Graph.Vertex))
            vertex = this.getVertex(vertex);
        this._vertices.delete(vertex.obj);
        this._edges = this._edges.filter(e => e.from !== vertex || e.to !== vertex);
        this._edgesToVertex.delete(vertex);
        for (const key of this._edgesToVertex.keys()) {
            this._edgesToVertex.set(key, this._edgesToVertex.get(key).filter(e => e.from !== vertex));
        }
    }

    /** @param {Graph.Edge} edge */
    removeEdge(edge) {
        const {from, to} = edge;
        for (const vertex of this._vertices.values()) {
            vertex.removeEdge(edge)
        }
        this._edges = this._edges.filter(e => e.from !== from && e.to !== to);
        for (const key of this._edgesToVertex.keys()) {
            this._edgesToVertex.set(key, this._edgesToVertex.get(key).filter(e => e.from !== from && e.to !== from));
        }
    }

    /**
     * @param {any} obj
     * @returns {Graph.Vertex || undefined}
     */
    getVertex(obj) {
        return this._vertices.get(obj);
    }

    /**
     * @param {any} vertex
     * @returns {boolean}
     */
    hasVertex(vertex) {
        let id = vertex;
        if (vertex instanceof Graph.Vertex)
            id = vertex.obj
        return this._vertices.has(id);
    }

    /**
     * @param {Graph.Edge} edge
     * @returns {boolean}
     */
    hasEdge(edge) {
        let edges = this._edgesToVertex.get(edge.to);
        if (!edges) return false;
        return edges.findIndex(e => e.from === edge.from) !== -1;
    }

    /**
     * @param {Graph.Edge} edge
     * @returns {boolean}
     */
    canCreateEdge(edge) {
        return !this.hasEdge(edge);
    }

    _getOrCreateVertex(obj) {
        let id = obj;
        if (obj instanceof Graph.Vertex) {
            id = obj.obj;
        }
        let vertex = this.getVertex(id);
        if (vertex)
            return vertex;
        return new Graph.Vertex(id);
    }
}

Graph.Vertex = class {
    /**
     * @param {any} obj
     */
    constructor(obj) {
        this.obj = obj;
        this.edges = [];
    }

    /** @param {Graph.Edge} edge */
    addEdge(edge) {
        if (!this.hasEdge(edge))
            this.edges.push(edge)
    }

    /** @param {Graph.Edge} edge */
    removeEdge(edge) {
        this.edges = this.edges.filter(e => e.from !== edge.from && e.to !== e.to)
    }

    hasEdge(edge) {
        return this.edges.findIndex(e => e.from === edge.from && e.to === edge.to) !== -1;
    }
}

Graph.Edge = class {
    /**
     * @param {Graph.Vertex} from
     * @param {Graph.Vertex} to
     */
    constructor(from, to) {
        this.from = from;
        this.to = to;
    }
}

/**
 * Directed Acyclic graph
 */
export class DAGraph extends Graph {

    /**
     * @param {Graph.Edge} edge
     * @returns {boolean}
     */
    canCreateEdge(edge) {
        if (this._createsCycles(edge))
            throw new Error("Can't create edge because it creates cycle")
        return !this.hasEdge(edge)
    }

    /**
     * @returns {Graph.Vertex[]}
     */
    getVerticesInReverseOrder() {
        const vertices = [...this._vertices.values()];
        const lastNodes = [];
        out: while (vertices.length > 0) {
            let current = vertices.shift();
            if (current.edges.length === 0) {
                lastNodes.push(current);
                continue;
            }
            for (const edge of current.edges) {
                if (edge.from === current)
                    continue out;
            }
            lastNodes.push(current);
        }
        /** @type {Set<Graph.Vertex>} */
        const result = new Set();
        while (lastNodes.length > 0) {
            let current = lastNodes.shift();
            result.add(current.obj);
            if (current.edges.length === 0)
                continue;
            loop: for (const edge of current.edges) {
                let from = edge.from;
                if (from !== current) {
                    if (from.edges.length > 1) {
                        for (const edgeFrom of from.edges) {
                            if (edgeFrom.from === from && edgeFrom.to !== current && !result.has(edgeFrom.to.obj)) {
                                continue loop;
                            }
                        }
                    }
                    lastNodes.push(from)
                }
            }
        }
        return [...result.values()];
    }

    /**
     * @param {Graph.Edge} edge
     * @returns {boolean}
     * @private
     */
    _createsCycles(edge) {
        const {from, to} = edge;
        const queue = [from];
        while (queue.length > 0) {
            const current = queue.shift();
            if (current.obj === to.obj)
                return true
            let edges = this._edgesToVertex.get(current);
            if (edges)
                for (const next of edges) {
                    queue.push(next.from)
                }
        }
        return false;
    }
}